import { DateRange, SplitBy, } from '@intentsify/types';
import chunk from 'lodash/chunk';
import { DateTime, Interval } from 'luxon';
/**
 * Shows ranges of weeks as a number.
 * */
const DateRangeToWeekValue = {
    [DateRange.LastFullWeek]: 1,
    [DateRange.LastTwoWeeks]: 2,
    [DateRange.LastFourWeeks]: 4,
    [DateRange.LastEightWeeks]: 8,
    [DateRange.LastTwelveWeeks]: 12,
};
const offsetDate = (date, offset) => {
    if (!offset || !offset.minus) {
        return date;
    }
    return date.minus(offset.minus);
};
/**
 * When users query for full date ranges, we need to take few things into account.
 *
 * 1. If start date is in the future, we just give them the last week of data, that's what they are
 * looking for anyway. We assume the start date is in the future if its after the start of the last week, as we only
 * have data up to that moment in time.
 *
 * 2. If end date is in the future but start date is not in the future we want to return
 * a valid date range but without any unnecessary future dates. So the end date becomes
 * the end of the previous week as we only have the data up to the previous week.
 *
 * 3. If campaign has ended, the full range will include all dates up to the end of the previous week.
 */
const getDatesForFullDateRangeQueries = (start) => {
    const isStartFutureDate = start.toUTC() > DateTime.now().minus({ week: 1 }).startOf('week').toUTC();
    if (isStartFutureDate) {
        return {
            start: DateTime.utc().minus({ week: 1 }).startOf('week'),
            end: DateTime.utc().minus({ week: 1 }).endOf('week'),
        };
    }
    return {
        start: start.toUTC().startOf('week'),
        end: DateTime.now().toUTC().minus({ week: 1 }).endOf('week'),
    };
};
/**
 * Attempt to figure out campaigns start and end date.
 * At the moment we cannot trust the data fully.
 */
export const validateAndGetCampaignStartAndEndDate = ({ startDate, endDate, }) => {
    // campaign has no dates at all
    if (!endDate && !startDate) {
        throw new Error(`Campaign has no startDate or endDate`);
    }
    if (endDate && startDate) {
        // has dates, but they are invalid
        if (startDate > endDate) {
            throw new Error(`Campaign has startDate after endDate`);
        }
        // has valid dates, just return them
        return {
            start: DateTime.fromJSDate(startDate).toUTC(),
            end: DateTime.fromJSDate(endDate).toUTC(),
        };
    }
    // doesn't have an endDate but has startDate; endDate = startDate + 1 year
    if (!endDate && startDate) {
        return {
            start: DateTime.fromJSDate(startDate).toUTC(),
            end: DateTime.fromJSDate(startDate).toUTC().plus({ years: 1 }),
        };
    }
    throw new Error(`Campaign has no startDate`);
};
/**
 * Limit the date range reach into the past if a campaign doesn't have historical data because it only started recently.
 * E.g.: if user selects last 8 weeks of data but campaign has only started 4 weeks ago,
 * we can safely default to total and disregard the other 4 weeks.
 * */
const getProperDateRange = (startDate, dateRange) => {
    const { weeks } = DateTime.now().diff(startDate, ['weeks']);
    const weeksDiff = weeks.toFixed(0);
    return dateRange !== DateRange.Custom &&
        dateRange !== DateRange.Full &&
        Number(weeksDiff) < Number(DateRangeToWeekValue[dateRange])
        ? DateRange.Full
        : dateRange;
};
/**
 * Converts a DateRangeEnum into relative START and END date objects.
 */
export const getDatesFromDateRange = (dateRange, startDate, endDate) => {
    const { start } = validateAndGetCampaignStartAndEndDate({
        startDate,
        endDate,
    });
    const selectedDateRange = getProperDateRange(start, dateRange);
    switch (true) {
        case selectedDateRange === DateRange.Full:
            return getDatesForFullDateRangeQueries(start);
        case selectedDateRange === DateRange.LastFullWeek:
            return {
                start: DateTime.utc().startOf('week').minus({ weeks: 1 }),
                end: DateTime.utc().startOf('week').minus({ weeks: 1 }).endOf('week'),
            };
        case selectedDateRange === DateRange.LastTwoWeeks:
            return {
                start: DateTime.utc().startOf('week').minus({ weeks: 2 }),
                end: DateTime.utc().startOf('week').minus({ weeks: 1 }).endOf('week'),
            };
        case selectedDateRange === DateRange.LastFourWeeks:
            return {
                start: DateTime.utc().startOf('week').minus({ weeks: 4 }),
                end: DateTime.utc().startOf('week').minus({ weeks: 1 }).endOf('week'),
            };
        case selectedDateRange === DateRange.LastEightWeeks:
            return {
                start: DateTime.utc().startOf('week').minus({ weeks: 8 }),
                end: DateTime.utc().startOf('week').minus({ weeks: 1 }).endOf('week'),
            };
        case selectedDateRange === DateRange.LastTwelveWeeks:
            return {
                start: DateTime.utc().startOf('week').minus({ weeks: 12 }),
                end: DateTime.utc().startOf('week').minus({ weeks: 1 }).endOf('week'),
            };
        default:
            return { start: DateTime.utc().startOf('month'), end: DateTime.utc() };
    }
};
const calculateDateRanges = ({ start, end, splitBy, }) => {
    switch (true) {
        case splitBy === SplitBy.Nothing: {
            const startOf = start.startOf('week');
            const endOf = end.endOf('week');
            const interval = Interval.fromDateTimes(startOf, endOf);
            const weeks = interval.splitBy({ weeks: 1 });
            const ranges = [];
            weeks.forEach((c) => {
                const week = c.start.weekNumber;
                const year = weeks.length === 1 ? c.end.year : c.start.year;
                const r = ranges.find((i) => i.yearNumber === year);
                if (r?.weekNumbers) {
                    r.weekNumbers.push(week);
                }
                else {
                    ranges.push({
                        yearNumber: year,
                        weekNumbers: [week],
                    });
                }
            });
            return {
                total: {
                    start: startOf.toUTC().toISO(),
                    end: endOf.toUTC().toISO(),
                },
                splits: [
                    {
                        ranges,
                        dateRange: {
                            start: start.startOf('week').toUTC().toISO(),
                            end: end.endOf('week').toUTC().toISO(),
                        },
                    },
                ],
            };
        }
        case splitBy === SplitBy.OneWeek: {
            const startOf = start.startOf('week');
            const endOf = end.endOf('week');
            const interval = Interval.fromDateTimes(startOf, endOf);
            const durationInWeek = Math.ceil(interval.length('weeks'));
            return {
                total: {
                    start: startOf.toUTC().toISO(),
                    end: endOf.toUTC().toISO(),
                },
                splits: Array(durationInWeek)
                    .fill(undefined)
                    .map((_, index) => {
                    const split = {
                        ranges: [
                            {
                                yearNumber: startOf.plus({ weeks: index }).year,
                                weekNumbers: [startOf.plus({ weeks: index }).weekNumber],
                            },
                        ],
                        dateRange: {
                            start: startOf.plus({ weeks: index }).toUTC().toISO(),
                            end: startOf
                                .plus({ weeks: index })
                                .endOf('week')
                                .toUTC()
                                .toISO(),
                        },
                    };
                    return split;
                }),
            };
        }
        case splitBy === SplitBy.FourWeeks: {
            const startOf = start.startOf('week');
            const endOf = end.endOf('week');
            const interval = Interval.fromDateTimes(startOf, endOf);
            const weeks = interval.splitBy({ weeks: 1 });
            const chunks = chunk(weeks, 4);
            return {
                total: {
                    start: startOf.toUTC().toISO(),
                    end: endOf.toUTC().toISO(),
                },
                splits: chunks.map((singleChunk) => {
                    const ranges = [];
                    singleChunk.forEach((c) => {
                        const week = c.start.weekNumber;
                        const year = c.start.year;
                        const r = ranges.find((i) => i.yearNumber === year);
                        if (r?.weekNumbers) {
                            r.weekNumbers.push(week);
                        }
                        else {
                            ranges.push({
                                yearNumber: year,
                                weekNumbers: [week],
                            });
                        }
                    });
                    const split = {
                        ranges,
                        dateRange: {
                            start: singleChunk[0].start.toUTC().toISO(),
                            end: singleChunk[singleChunk.length - 1].start
                                .endOf('week')
                                .toUTC()
                                .toISO(),
                        },
                    };
                    return split;
                }),
            };
        }
        default:
            return {
                splits: [],
                total: {
                    start: start.toISO(),
                    end: end.toISO(),
                },
            };
    }
};
/**
 * Creates a QueryDateRange object from a DateRangeEnum and SplitBy to be used to generate a SQL query.
 *
 * @example
 * If I want to have 8 weeks of data split by 4 weeks and its the begging of 2021 it would give you these splits:
 *
 * # Usage
 * ```ts
 * const dateRange = getDateRange(
 *   DateRange.LastEightWeeks,
 *   SplitBy.FourWeeks,
 *   new Date("2020-12-20T23:00:00.000Z"),
 *   new Date("2021-01-17T22:59:59.999Z")
 * )
 * ```
 *
 * # Result
 * ```ts
 * [
 *   {
 *     ranges: [
 *       {
 *         yearNumber: 2020,
 *         weekNumbers: [
 *           48,
 *           49,
 *           50,
 *           51,
 *         ],
 *       },
 *     ],
 *     dateRange: {
 *       start: "2020-11-22T23:00:00.000Z",
 *       end: "2020-12-20T22:59:59.999Z",
 *     },
 *   },
 *   {
 *     ranges: [
 *       {
 *         yearNumber: 2020,
 *         weekNumbers: [
 *           52,
 *           53,
 *         ],
 *       },
 *       {
 *         yearNumber: 2021,
 *         weekNumbers: [
 *           1,
 *           2,
 *         ],
 *       },
 *     ],
 *     dateRange: {
 *       start: "2020-12-20T23:00:00.000Z",
 *       end: "2021-01-17T22:59:59.999Z",
 *     },
 *   },
 * ]
 * ```
 */
export const getDateRange = ({ range, splitBy, startDate, endDate, offset, customDateRange, }) => {
    if (range === DateRange.Custom && typeof customDateRange === 'undefined') {
        throw new Error('When rage is set to "custom", customDateRange needs to be provided');
    }
    const { start, end } = getDatesFromDateRange(range, startDate, endDate);
    if (!start) {
        return {
            splits: [],
            total: {
                start: DateTime.utc().toISO(),
                end: DateTime.utc().toISO(),
            },
        };
    }
    return calculateDateRanges({
        start: offsetDate(start, offset),
        end: offsetDate(end, offset),
        splitBy,
    });
};
export const getCustomDateRange = ({ splitBy, customDateRange, }) => {
    return calculateDateRanges({
        start: customDateRange.start,
        end: customDateRange.end,
        splitBy,
    });
};
export const getLastWeekAndYearNumbers = () => {
    const { weekNumber, year } = DateTime.now().minus({ weeks: 1 });
    return {
        weekNumber: weekNumber,
        yearNumber: year,
    };
};
//# sourceMappingURL=dateRange.js.map